"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TakeoffTooBig = void 0;
exports.planTakeoff = planTakeoff;
exports.takeoffFileName = takeoffFileName;
// An option, priced, written into a blank STM002 Repaint Take Off (#516): the
// inputs an estimator types, never the workbook's sums — its own formulas do
// the pricing when Excel opens it. The reverse of the importer
// (m2q-takeoff-import): the same block finder (takeoff-layout.ts), the cells
// it reads written instead (takeoff-sheet.ts says how; takeoff-header.ts the
// header).
//
// Every figure the template works out per block is a straight multiple of what
// is in that block (hours, litres, the percentages of its hours, the fuel levy),
// so an area too big for one block carries on in the next one ("… (cont.)")
// and still prices the same. A job too big for one workbook — 20 internal
// blocks, 20 external, six paints — carries on in the next file; so does an
// area that alone uses more than six paints.
const file_name_ts_1 = require("./file-name.js");
const takeoff_paints_ts_1 = require("./takeoff-paints.js");
const takeoff_header_ts_1 = require("./takeoff-header.js");
const takeoff_sheet_ts_1 = require("./takeoff-sheet.js");
const CONTINUED = ' (cont.)';
const APPROVED_LABEL = 'Approved hours adjustment';
const DOWNTIME_ON = 'Yes';
/** Template surface labels that aren't the app type's name (to-app.ts maps them the other way). */
const LABEL_OF_TYPE = {
    'windows(lm)–internal': 'windows(lm)',
    'windows(lm)–external': 'windows(lm)',
    wallsrough: 'wallsrough(adjustspreadrate)',
};
const labelKey = (typeName) => LABEL_OF_TYPE[(0, takeoff_sheet_ts_1.squash)(typeName)] ?? (0, takeoff_sheet_ts_1.squash)(typeName);
function pendingOf(section) {
    const input = section.input;
    const count = Math.max(1, input.buildingCount ?? 1);
    const rows = input.surfaces.map((row) => {
        const typeName = section.typeNames[row.systemOf ?? row.id] ?? row.name;
        const owner = row.systemOf ? input.surfaces.find((s) => s.id === row.systemOf) : undefined;
        const label = owner ? `${owner.name} – ${row.name}` : row.name;
        return { row, match: row.systemOf ? '' : labelKey(typeName), label, renamed: (0, takeoff_sheet_ts_1.squash)(label) !== (0, takeoff_sheet_ts_1.squash)(typeName) };
    });
    // Approved hours replace the surfaces' own: the difference goes on a row of its own, labour only (no girth, so no paint).
    const measured = input.surfaces.reduce((sum, s) => sum + (0, takeoff_paints_ts_1.surfaceHours)(s), 0);
    const approved = typeof input.approvedHours === 'number' && !(0, takeoff_sheet_ts_1.same)(input.approvedHours, measured) ? input.approvedHours - measured : 0;
    if (approved) {
        const row = { id: 'approved', name: APPROVED_LABEL, unit: 'h', quantity: approved, girth: 0, coats: 1, rate: 1, prep: 0, spread: 1 };
        rows.push({ row, match: '', label: APPROVED_LABEL, renamed: true, approved: true });
    }
    const sundries = [...(input.sundries ?? [])];
    return { section, count, surfaces: rows, sundries, hire: [...(input.access ?? [])], trades: [...(input.otherTrades ?? [])], first: !section.continued };
}
// ---------- filling ----------
/** The blocks of each side, in order down the sheet. */
const blocksOf = (layout, side) => layout.blocks.filter((b) => b.side === side);
/** Place a surface row into one free surface row of the block; false when the block has none left. */
function placeSurface(sheet, ctx, item, free, count) {
    const labelAt = (r) => (0, takeoff_sheet_ts_1.squash)(ctx.template.text(takeoff_sheet_ts_1.T, `A${r}`));
    const own = item.match ? free.find((r) => labelAt(r) === item.match) : undefined;
    const at = own ?? free.find((r) => labelAt(r) === 'other') ?? free[0];
    if (at === undefined)
        return false;
    free.splice(free.indexOf(at), 1);
    const s = item.row;
    if (own === undefined || item.renamed)
        sheet.text(`A${at}`, item.label);
    // Sprayed: the template has no uplift, so it is in the rates (hours = q × coats ÷ rate + q ÷ prep, all × (1 + uplift)).
    const uplift = 1 + (s.uplift ?? 0);
    sheet.number(`B${at}`, s.pitchInQuantity ? 1 : s.girth);
    sheet.cells.set(`C${at}`, s.quantity * count);
    sheet.number(`D${at}`, s.coats);
    sheet.number(`E${at}`, (0, takeoff_sheet_ts_1.perHour)(s.rate / uplift));
    sheet.number(`F${at}`, (0, takeoff_sheet_ts_1.perHour)(s.spread));
    sheet.number(`M${at}`, s.prep > 0 ? s.prep / uplift : 0);
    if (item.approved)
        return true;
    const slot = sheet.slots.get((0, takeoff_paints_ts_1.paintOf)(s, ctx.rates));
    // byPaint keeps every area within the six slots, so a paint always has one.
    if (slot === undefined)
        throw new Error(`"${s.name}" has no paint slot in this workbook.`);
    if (slot > 0)
        sheet.formula(`G${at}`, (0, takeoff_sheet_ts_1.slotFormula)(slot));
    return true;
}
/** The area-wide settings every block of an area carries: its shares of hours and its rates. */
function blockSettings(sheet, ctx, b, input) {
    if (input.downtime) {
        sheet.text(`C${b.downtime}`, DOWNTIME_ON);
        sheet.number(`E${b.downtime}`, input.downtime);
    }
    if (b.consumables !== null && input.consumablesRate !== undefined)
        sheet.number(`E${b.consumables}`, input.consumablesRate);
    if (b.wash !== null && input.washRate !== undefined)
        sheet.number(`E${b.wash}`, (0, takeoff_sheet_ts_1.perHour)(input.washRate));
    if (b.fuelLevy !== null)
        sheet.number(`D${b.fuelLevy}`, ctx.rates.fuelLevyPerHour);
    const share = (row, value) => {
        if (row !== null && value)
            sheet.number(`G${row}`, value);
    };
    share(b.overtime, input.overtime);
    share(b.foreman, input.foreman);
    share(b.abseil, input.abseil);
}
/** Notes rows: the area's exclusions on the block's "Excludes" row, its scope notes on its "Note" row. */
function blockNotes(sheet, ctx, b, section) {
    const label = (r) => ctx.template.text(takeoff_sheet_ts_1.T, `A${r}`).toLowerCase();
    const pick = (exact, loose) => b.notes.find((r) => exact.test(label(r))) ?? b.notes.find((r) => loose.test(label(r)));
    const excludes = pick(/^excludes/, /exclud/);
    const note = pick(/^(general )?note/, /note/);
    if (section.exclusions && excludes !== undefined)
        sheet.text(`B${excludes}`, section.exclusions);
    if (section.notes && note !== undefined)
        sheet.text(`B${note}`, section.notes);
}
/** Fill as much of the area as fits into one block; what doesn't stays pending. */
function fillBlock(sheet, ctx, b, pending) {
    const { section, count } = pending;
    sheet.blocks.push({ block: b.number, sectionId: section.input.id });
    sheet.text(`A${b.header + 1}`, pending.first ? section.name : `${section.name}${CONTINUED}`);
    blockSettings(sheet, ctx, b, section.input);
    const free = [...b.surfaces];
    // Rows that match a template label claim it first; the rest take the "Other" rows, then any left.
    const order = [...pending.surfaces].sort((x, y) => Number(!x.match) - Number(!y.match));
    pending.surfaces = order.filter((item) => !placeSurface(sheet, ctx, item, free, count));
    const sundryRows = [...b.sundries];
    while (pending.sundries.length && sundryRows.length) {
        const line = pending.sundries.shift();
        const r = sundryRows.shift();
        sheet.text(`A${r}`, line.description);
        sheet.cells.set(`C${r}`, line.items * count);
        sheet.number(`D${r}`, 1);
        sheet.number(`E${r}`, line.minutes);
        sheet.number(`F${r}`, line.spread || 1);
    }
    const hireSlots = [...b.hire];
    for (const line of [...pending.hire]) {
        // A slot without a Fuel row (rev 41's block 2, second item) only takes a line that burns no fuel.
        const slot = hireSlots.find((h) => h.cartage.length && (h.fuel !== null || !line.fuelPerWeek));
        if (!slot)
            break;
        hireSlots.splice(hireSlots.indexOf(slot), 1);
        pending.hire.splice(pending.hire.indexOf(line), 1);
        sheet.cells.set(`A${slot.day}`, line.item);
        // The template looks rates up by the item's name, approximately; the app's rates are typed over it.
        sheet.cells.set(`D${slot.day}`, line.dayRate);
        sheet.cells.set(`G${slot.day}`, line.days * count);
        sheet.cells.set(`D${slot.cartage[0]}`, line.transport);
        sheet.cells.set(`G${slot.cartage[0]}`, line.trips * count);
        if (slot.fuel !== null)
            sheet.cells.set(`D${slot.fuel}`, line.fuelPerWeek);
    }
    const tradeRows = [...b.otherTrades];
    while (pending.trades.length && tradeRows.length) {
        const line = pending.trades.shift();
        const r = tradeRows.shift();
        sheet.text(`A${r}`, line.description);
        sheet.cells.set(`G${r}`, line.qty * count);
        sheet.cells.set(`D${r}`, line.rate);
    }
    if (pending.first) {
        const input = section.input;
        if (b.wash !== null && input.washArea)
            sheet.cells.set(`C${b.wash}`, input.washArea * count);
        if (b.scaffold !== null && input.scaffoldMoves?.count) {
            sheet.cells.set(`C${b.scaffold}`, input.scaffoldMoves.count * count);
            sheet.number(`D${b.scaffold}`, 1);
            sheet.number(`E${b.scaffold}`, input.scaffoldMoves.minutes);
        }
        blockNotes(sheet, ctx, b, section);
    }
    pending.first = false;
}
/** Done once nothing is left and the area has at least one block (an area with nothing in it still gets its named block). */
const isDone = (p, placed) => !p.surfaces.length && !p.sundries.length && !p.hire.length && !p.trades.length && placed > 0;
/**
 * Write one area into the workbook from the next free block of its side.
 * False (and nothing written) when it doesn't fit: out of blocks, or a paint
 * that needs a seventh slot.
 */
function placeSection(sheet, ctx, blocks, next, section) {
    const trial = sheet.fork();
    if (![...(0, takeoff_paints_ts_1.paintsUsed)(section, ctx.rates)].every((p) => trial.addPaint(p)))
        return false;
    const pending = pendingOf(section);
    let at = next.get(section.side) ?? 0;
    let placed = 0;
    while (!isDone(pending, placed)) {
        const b = blocks[at++];
        if (!b)
            return false;
        fillBlock(trial, ctx, b, pending);
        placed++;
    }
    next.set(section.side, at);
    for (const [k, v] of trial.cells)
        sheet.cells.set(k, v);
    sheet.blocks.push(...trial.blocks);
    for (const [k, v] of trial.slots)
        sheet.slots.set(k, v);
    return true;
}
// ---------- the whole option ----------
/** One area needs more blocks of its side than a workbook has. */
class TakeoffTooBig extends Error {
}
exports.TakeoffTooBig = TakeoffTooBig;
/**
 * The option as one or more workbooks' Take Off cells. Areas fill blocks in
 * the app's order; a new workbook starts when the next area won't fit. Throws
 * when one area alone needs more blocks than a workbook has.
 */
function planTakeoff(fill) {
    const { template, layout, rates, job } = fill;
    const ctx = { rates, template };
    const slots = new Map([[rates.paints[0]?.id ?? '', 0]]);
    const sides = { Internal: blocksOf(layout, 'Internal'), External: blocksOf(layout, 'External') };
    const files = [];
    const start = () => {
        files.push({ sheet: new takeoff_sheet_ts_1.Sheet(template, slots), next: new Map() });
        return files[files.length - 1];
    };
    let current = start();
    for (const section of fill.sections.flatMap((s) => (0, takeoff_paints_ts_1.byPaint)(s, rates))) {
        if (placeSection(current.sheet, ctx, sides[section.side], current.next, section))
            continue;
        const fresh = current.sheet.blocks.length ? start() : current;
        if (!placeSection(fresh.sheet, ctx, sides[section.side], fresh.next, section)) {
            throw new TakeoffTooBig(`"${section.name}" is too big for one take-off workbook: it needs more than ${sides[section.side].length} ${section.side.toLowerCase()} area blocks.`);
        }
        current = fresh;
    }
    return files.map(({ sheet }) => {
        (0, takeoff_header_ts_1.writeHeader)(sheet, template, rates, job);
        return { cells: sheet.cells, blocks: sheet.blocks };
    });
}
/**
 * "SA2007 Take Off - Option A (1 of 2).xlsx"; one file has no "(1 of 1)". A name
 * longer than `maxLength` (the place it's saved caps names) is shortened before
 * the "(1 of 2)", never through it, and ends with a tag from the whole name
 * ("… -1x9k2a.xlsx"): the name reported is the name saved, and two long names
 * never land on one file. A name that fits is left as it is.
 */
function takeoffFileName(jobNumber, optionName, part, of, maxLength = Number.POSITIVE_INFINITY) {
    const stem = `${jobNumber} Take Off${optionName ? ` - Option ${optionName}` : ''}`;
    const suffix = `${of > 1 ? ` (${part} of ${of})` : ''}.xlsx`;
    return (0, file_name_ts_1.fitFileName)([{ shortenable: stem }, suffix], maxLength, { tagged: true });
}
